import React, { useState, useEffect, useRef, useMemo } from "react";
import { FaPlus, FaEdit, FaTrash, FaLock } from "react-icons/fa";
import "./css/AbsencePopup.css";
import { BACKEND_URL } from "../constants";
import axios from "axios";
import { useAuth } from "../navigation/AuthProvider";
import { SickPanel } from "./absence/specialPanels/SickPanel";

export const AbsencePopup = ({ onClose, readOnly }) => {
  // ✅ setCompany är optional – om du inte har den i AuthProvider så funkar det ändå
  const { user, company, setCompany, absenceRules, setAbsenceRules } = useAuth();

  const [allAbsences, setAllAbsences] = useState(absenceRules || []);
  const [editingAbsence, setEditingAbsence] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const firstItemRef = useRef(null);
  const listRef = useRef(null);
  const [rowHeight, setRowHeight] = useState(null);

  // ✅ NEW: loading states for better UX
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    setAllAbsences(absenceRules || []);
  }, [absenceRules]);

  useEffect(() => {
    if (firstItemRef.current) {
      const h = firstItemRef.current.getBoundingClientRect().height;
      if (h && h > 0) setRowHeight(Math.ceil(h));
    }
  }, [allAbsences, showForm]);

  const sortedAbsences = useMemo(() => {
    const arr = Array.isArray(allAbsences) ? allAbsences.slice() : [];
    return arr.sort((a, b) => {
      const al = a?.locked ? 1 : 0;
      const bl = b?.locked ? 1 : 0;
      if (al !== bl) return bl - al;
      const an = (a?.name || "").toString();
      const bn = (b?.name || "").toString();
      return an.localeCompare(bn, "sv-SE");
    });
  }, [allAbsences]);

  const handleAdd = () => {
    setEditingAbsence(null);
    setShowForm(true);
  };

  const handleEdit = async (absence) => {
    setEditingAbsence(absence);
    setShowForm(true);
  };

  const handleDelete = async (absenceId) => {
    const abs = (allAbsences || []).find((a) => String(a?._id) === String(absenceId));
    if (abs?.locked) return;

    if (!window.confirm("Är du säker på att du vill ta bort denna frånvarotyp?")) return;

    try {
      setDeletingId(String(absenceId));

      const { data } = await axios.delete(`${BACKEND_URL}/delete-absence-type`, {
        data: {
          userId: user._id,
          companyId: company._id,
          absenceId,
        },
      });

      if (data.status === "success") {
        setAllAbsences((prev) => prev.filter((a) => a._id !== absenceId));
        setAbsenceRules((prev) => prev.filter((a) => a._id !== absenceId));
      } else {
        return alert(data.errorText || "Kunde inte ta bort frånvarotyp");
      }
    } catch (err) {
      console.error("❌ handleDelete error:", err);
      alert("Ett fel uppstod vid borttagning.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleSaveAbsence = async (newAbsence) => {
    if (!newAbsence.name || isNaN(newAbsence.percentage)) {
      return alert("Namn och procent krävs");
    }

    try {
      if (editingAbsence && editingAbsence._id) {
        const { data } = await axios.put(`${BACKEND_URL}/edit-absence-type`, {
          userId: user._id,
          companyId: company._id,
          absenceId: editingAbsence._id,
          updated: newAbsence,
        });

        if (data.status === "success") {
          // update absence lists
          setAllAbsences((prev) =>
            prev.map((a) => (a._id === editingAbsence._id ? data.updatedAbsence : a))
          );
          setAbsenceRules((prev) =>
            prev.map((a) => (a._id === editingAbsence._id ? data.updatedAbsence : a))
          );

          // ✅ if backend also returned updatedCompany, update auth state
          if (data.updatedCompany && typeof setCompany === "function") {
            setCompany(data.updatedCompany);
          }
        } else {
          return alert(data.errorText || "Kunde inte uppdatera frånvarotyp");
        }
      } else {
        const { data } = await axios.post(`${BACKEND_URL}/create-absence-type`, {
          userId: user._id,
          companyId: company._id,
          absence: newAbsence,
        });

        if (data.status === "success") {
          setAllAbsences((prev) => [...prev, data.newAbsence]);
          setAbsenceRules((prev) => [...prev, data.newAbsence]);
        } else {
          return alert(data.errorText || "Kunde inte skapa ny frånvarotyp");
        }
      }

      setShowForm(false);
    } catch (err) {
      console.error("❌ handleSaveAbsence error:", err);
      alert("Ett fel uppstod vid sparande.");
    }
  };

  const shouldScroll = allAbsences.length > 5;
  const computedMaxHeight = shouldScroll && rowHeight ? `${rowHeight * 5.5}px` : "none";

  return (
    <div className="absence-popup">
      <div className="absence-popup__overlay" onClick={onClose} />
      <div className="absence-popup__content">
        <h2 className="absence-popup__title">Frånvarotyper</h2>
        <p className="absence-popup__description">
          Skapa frånvarotyper och hur mycket lön de ger i %. Används på arbetspass och i löneunderlag.
        </p>

        <div
          className="absence-popup__list"
          role="list"
          ref={listRef}
          style={{
            maxHeight: computedMaxHeight,
            overflowY: shouldScroll ? "auto" : "visible",
            paddingRight: 6,
            borderRadius: 10,
          }}
        >
          {sortedAbsences.map((absence, index) => {
            const isDeleting = String(deletingId || "") === String(absence?._id || "");
            return (
              <div
                key={absence?._id || index}
                className="absence-popup__item"
                ref={index === 0 ? firstItemRef : null}
                role="listitem"
                style={{
                  minHeight: rowHeight ? `${rowHeight}px` : undefined,
                  boxSizing: "border-box",
                  opacity: isDeleting ? 0.7 : 1,
                }}
              >
                <div className="absence-popup__item-left">
                  <div className="absence-popup__item-title">
                    {absence?.name}
                    {absence?.locked ? <span className="absence-popup__badge">Låst</span> : null}
                  </div>
                  <div className="absence-popup__item-sub">
                    {absence?.percentage}% {absence?.description ? `• ${absence.description}` : ""}
                  </div>
                </div>

                <div className="absence-popup__actions">
                  {!readOnly &&(
                  <>
                    <button
                      className="icon-btn"
                      onClick={() => handleEdit(absence)}
                      title="Redigera"
                      disabled={isDeleting}
                    >
                      <FaEdit />
                    </button>

                    {absence?.locked ? (
                      <button className="icon-btn icon-btn--disabled" title="Låst" disabled>
                        <FaLock />
                      </button>
                    ) : (
                      <button
                        className="icon-btn icon-btn--danger"
                        onClick={() => handleDelete(absence._id)}
                        title={isDeleting ? "Tar bort..." : "Ta bort"}
                        disabled={isDeleting}
                      >
                        {isDeleting ? "..." : <FaTrash />}
                      </button>
                    )}
                  </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="absence-popup__footer">
          {!readOnly && (
            <button className="btn btn--primary" onClick={handleAdd}>
              <FaPlus /> Lägg till ny frånvaro
            </button>
          )}
        </div>

        {showForm && (
          <AbsenceFormPopup
            absence={editingAbsence}
            onClose={() => setShowForm(false)}
            onSave={handleSaveAbsence}
            user={user}
            company={company}
            setCompany={setCompany}
          />
        )}
      </div>
    </div>
  );
};

const AbsenceFormPopup = ({ absence, onClose, onSave, user, company, setCompany }) => {
  const [name, setName] = useState(absence ? absence.name : "");
  const [percentage, setPercentage] = useState(absence ? absence.percentage : "");
  const [description, setDescription] = useState(absence ? absence.description : "");

  // ✅ saving/loading indicator for the primary button
  const [isSaving, setIsSaving] = useState(false);

  // ✅ keep draft (still exists so SickPanel can be re-enabled later without refactor)
  const [karensSettingsDraft, setKarensSettingsDraft] = useState(null);

  // ✅ strict boolean
  const karensEnabledFromCompany = Boolean(company?.payrollSettings?.karensEnabled === true);

  // ✅ TEMP: keep the code but do not render SickPanel
  // Flip this to true when you're ready to ship karens UI.
  const ENABLE_SICKPANEL_UI = false;

  useEffect(() => {
    if (absence) {
      setName(absence.name);
      setPercentage(absence.percentage);
      setDescription(absence.description || "");

      if (absence.systemKey === "sick") {
        setKarensSettingsDraft(
          absence.karensSettings && typeof absence.karensSettings === "object"
            ? absence.karensSettings
            : null
        );
      } else {
        setKarensSettingsDraft(null);
      }
    } else {
      setName("");
      setPercentage("");
      setDescription("");
      setKarensSettingsDraft(null);
    }
  }, [absence]);

  const isSick = absence?.systemKey === "sick";

  // ✅ Only treat as "used" when we actually render it.
  // This controls BOTH width and whether we include karensSettings in payload.
  const useSickPanel = Boolean(ENABLE_SICKPANEL_UI && isSick);

  const persistKarensEnabled = async (next) => {
    // keep function for later, but it won't be reachable when ENABLE_SICKPANEL_UI=false
    const nextBool = next === true; // strict
    if (isSaving) return false;

    try {
      if (!absence?._id) {
        alert("Kan inte ändra karens: frånvarotyp saknar id.");
        return false;
      }

      setIsSaving(true);

      const { data } = await axios.put(`${BACKEND_URL}/edit-absence-type`, {
        userId: user?._id,
        companyId: company?._id,
        absenceId: absence._id,
        updated: {
          companyPayrollSettings: { karensEnabled: nextBool },
        },
      });

      if (data?.status !== "success") {
        alert(data?.errorText || "Kunde inte spara karens-inställningen");
        return false;
      }

      if (data.updatedCompany && typeof setCompany === "function") {
        setCompany(data.updatedCompany);
      }

      return true;
    } catch (err) {
      console.error("❌ persistKarensEnabled error:", err);
      alert("Ett fel uppstod när karens-inställningen skulle sparas.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (isSaving) return;

    if (!name || percentage === "" || percentage === null || percentage === undefined) {
      return alert("Namn och procent krävs");
    }

    if (isNaN(Number(percentage))) {
      return alert("Procent måste vara en siffra");
    }

    const payload = {
      name,
      percentage: parseFloat(percentage),
      description,
    };

    // ✅ Only send karensSettings when SickPanel UI is actually enabled + used
    if (useSickPanel && karensSettingsDraft) {
      payload.karensSettings = karensSettingsDraft;
    }

    try {
      setIsSaving(true);
      await onSave(payload);
    } finally {
      setIsSaving(false);
    }
  };

  const buttonLabel = absence ? "Spara" : "Skapa";
  const savingLabel = absence ? "Sparar..." : "Skapar...";

  return (
    <div className="absence-form">
      <div className="absence-form__overlay" onClick={isSaving ? undefined : onClose} />

      {/* ✅ max 480px by default; only wide when SickPanel is actually rendered */}
      <div className={`absence-form__content ${useSickPanel ? "absence-form__content--wide" : ""}`}>
        <div className="absence-form__header">
          <div className="absence-form__header-title">
            {absence ? "Redigera frånvaro" : "Ny frånvarotyp"}
          </div>
          <div className="absence-form__header-subtitle">Ändringar sparas när du klickar Spara.</div>
        </div>

        <div className="absence-form__body">
          <div className="absence-form__layout">
            <div className="absence-form__left">
              <div className="absence-form__field">
                <label>Namn</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="t.ex. Sjukfrånvaro"
                  disabled={isSaving}
                />
              </div>

              <div className="absence-form__field">
                <label>Frånvarolön (i % av timlönen)</label>
                <div className="help-text">T.ex. 100 = full lön, 0 = ingen lön.</div>
                <input
                  type="number"
                  step="1"
                  min="0"
                  value={percentage}
                  onChange={(e) => setPercentage(e.target.value)}
                  placeholder="t.ex. 80"
                  disabled={isSaving}
                />
              </div>

              <div className="absence-form__field">
                <label>Beskrivning (valfri)</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="t.ex. Gäller vid sjukdom"
                  disabled={isSaving}
                />
              </div>
            </div>

            {/* ✅ Keep code, do not render unless enabled */}
            {useSickPanel && (
              <div className="absence-form__right">
                <SickPanel
                  absence={absence}
                  value={karensSettingsDraft}
                  onChange={setKarensSettingsDraft}
                  karensEnabled={karensEnabledFromCompany}
                  onToggleKarensEnabled={persistKarensEnabled}
                />
              </div>
            )}
          </div>
        </div>

        <div className="absence-form__footer">
          <button className="btn btn--secondary" onClick={onClose} disabled={isSaving}>
            Avbryt
          </button>
          <button className="btn btn--primary" onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? savingLabel : buttonLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
